// Structural East vs West (EVW) data mirrored from the ArmProgress app's `evw_*`
// tables. Translatable labels (weight class, arm, format, match title, results,
// prose) live in the locale JSON under `eastVsWest`; this module holds only the
// non-translatable structure (athletes, matchups, ranking snapshot).

export interface Athlete {
  name: string;
  nickname?: string;
  flag: string; // emoji flag
  country: string;
  image?: string; // self-hosted headshot; falls back to an illustrated avatar
}

export type WcKey =
  | 'superHeavyweight' | 'heavyweight' | 'lightHeavyweight' | 'middleweight'
  | 'welterweight' | 'lightweight' | 'featherweight' | 'open'
  | 'womensLightweight' | 'womensOpen';
export type TitleKey = 'hwSupermatch' | 'worldTitle' | 'interimWorldTitle' | 'supermatch' | 'fwSupermatch';
export type ArmKey = 'left' | 'right';
export type FormatKey = 'bo5' | 'bo7';

export interface Match {
  a: Athlete;
  b: Athlete;
  wc: WcKey;
  arm: ArmKey;
  format: FormatKey;
  title: TitleKey;
  mainEvent?: boolean;
}

export interface RankRow {
  pos: number;
  athlete: Athlete;
  champion?: boolean;
  trend?: 'up' | 'down' | 'same' | 'new';
}

// ── Athletes from the EVW 27 card (reuse for EVW 28 once announced) + ranking snapshot; real headshots self-hosted,
// the rest fall back to the illustrated avatar. Sourced from the published
// EVW 27 card (goldsarm.com / evwsports.com / nolimitsarmwrestling.com). ──
const A = {
  laletin: { name: 'Vitaly Laletin', flag: '🇷🇺', country: 'Russia', image: '/images/athletes/laletin.webp' },
  jablonski: { name: 'Kamil Jabłoński', flag: '🇵🇱', country: 'Poland', image: '/images/athletes/jablonski.webp' },
  larratt: { name: 'Devon Larratt', nickname: 'No Limits', flag: '🇨🇦', country: 'Canada', image: '/images/athletes/larratt.webp' },
  sagov: { name: 'Ibragim Sagov', flag: '🇷🇺', country: 'Russia', image: '/images/athletes/sagov.jpg' },
  chaffee: { name: 'Dave Chaffee', flag: '🇺🇸', country: 'USA', image: '/images/athletes/chaffee.jpg' },
  petrenko: { name: 'Oleg Petrenko', flag: '🇺🇦', country: 'Ukraine', image: '/images/athletes/oleg-petrenko.jpg' },
  hutchings: { name: 'Todd Hutchings', flag: '🇺🇸', country: 'USA', image: '/images/athletes/hutchings.jpg' },
  teliatnik: { name: 'Alexander Teliatnik', flag: '🇺🇦', country: 'Ukraine', image: '/images/athletes/teliatnik.jpg' },
  samushia: { name: 'Davit Samushia', flag: '🇬🇪', country: 'Georgia', image: '/images/athletes/samushia.jpg' },
  amolins: { name: 'Jānis Amoliņš', flag: '🇱🇻', country: 'Latvia', image: '/images/athletes/amolins.jpg' },
  bubenko: { name: 'Eldar Bubenko', flag: '🇷🇺', country: 'Russia', image: '/images/athletes/bubenko.jpg' },
  tavberidze: { name: 'Zurab Tavberidze', flag: '🇬🇪', country: 'Georgia', image: '/images/athletes/tavberidze.jpg' },
  ivanfi: { name: 'Brigitta Ivánfi', flag: '🇭🇺', country: 'Hungary', image: '/images/athletes/ivanfi.jpg' },
  kleinsmith: { name: 'Malin Kleinsmith', flag: '🇸🇪', country: 'Sweden', image: '/images/athletes/kleinsmith.jpg' },
  mask: { name: 'Matt Mask', flag: '🇨🇦', country: 'Canada', image: '/images/athletes/mask.jpg' },
  kalinichenko: { name: 'Sergei Kalinichenko', flag: '🇺🇦', country: 'Ukraine', image: '/images/athletes/kalinichenko.jpg' },
  roman: { name: 'Daniyar Roman', flag: '🇷🇺', country: 'Russia', image: '/images/athletes/roman.jpg' },
  bottaDukat: { name: 'Gábor Botta-Dukát', flag: '🇭🇺', country: 'Hungary', image: '/images/athletes/botta-dukat.jpg' },
  kurdecha: { name: 'Alex Kurdecha', flag: '🇵🇱', country: 'Poland', image: '/images/athletes/kurdecha.jpg' },
  tsvetkov: { name: 'Georgi Tsvetkov', flag: '🇧🇬', country: 'Bulgaria', image: '/images/athletes/tsvetkov.jpg' },
  holland: { name: 'Tom Holland', flag: '🇬🇧', country: 'United Kingdom', image: '/images/athletes/tom-holland.jpg' },
  peridis: { name: 'Petros Peridis', flag: '🇬🇷', country: 'Greece', image: '/images/athletes/peridis.jpg' },
  lupsa: { name: 'Andrei Lupsa', flag: '🇷🇴', country: 'Romania', image: '/images/athletes/lupsa.jpg' },
  fahlgren: { name: 'Erik Fahlgren', flag: '🇸🇪', country: 'Sweden', image: '/images/athletes/fahlgren.jpg' },
  // Ranking-only (not on this card):
  saginashvili: { name: 'Levan Saginashvili', flag: '🇬🇪', country: 'Georgia', image: '/images/athletes/saginashvili.webp' },
  gasparini: { name: 'Ermes Gasparini', flag: '🇮🇹', country: 'Italy', image: '/images/athletes/gasparini.webp' },
} satisfies Record<string, Athlete>;

export const EVENT = {
  name: 'East vs West 28',
  city: 'Las Vegas',
  country: 'USA',
  venue: 'Las Vegas, Nevada, USA',
  isoDate: '2026-12-19',
  multiplier: 1.5,
};

// ── EVW 28 match card, Las Vegas — not announced yet (empty → the page shows a
// "card to be announced" note). EVW 27 (Budapest) results live in EVENT_ARCHIVE. ──
export const MATCH_CARD: Match[] = [];

// ── Official ranking snapshots: Super Heavyweight, both arms ──
export interface Ranking {
  wc: WcKey;
  arm: ArmKey;
  rows: RankRow[];
}

export const RANKINGS: Ranking[] = [
  {
    wc: 'superHeavyweight',
    arm: 'right',
    rows: [
      { pos: 1, athlete: A.saginashvili, champion: true, trend: 'same' },
      { pos: 2, athlete: A.laletin, trend: 'up' },
      { pos: 3, athlete: A.larratt, trend: 'down' },
      { pos: 4, athlete: A.jablonski, trend: 'up' },
      { pos: 5, athlete: A.gasparini, trend: 'down' },
    ],
  },
  {
    wc: 'superHeavyweight',
    arm: 'left',
    rows: [
      { pos: 1, athlete: A.larratt, champion: true, trend: 'same' },
      { pos: 2, athlete: A.saginashvili, trend: 'same' },
      { pos: 3, athlete: A.chaffee, trend: 'up' },
      { pos: 4, athlete: A.gasparini, trend: 'down' },
    ],
  },
];
